import React, {useEffect, useState} from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import {useAuth} from "../contexts/AuthContext";
import Cookies from 'js-cookie';
import {jwtDecode} from 'jwt-decode';
import {DecodedToken, UserInfo} from "../types/interfaces";
import logo from "../assets/btc_logo2.png";
import {submitLogin} from "../api/login.api";


const LoginPage: React.FC = () => {
    const [showLoginError, setShowLoginError] = useState(false);
    const navigate = useNavigate();
    const  {logIn} = useAuth();

    useEffect(() => {
        sessionStorage.clear();
    }, []); // Empty dependency array = runs only once

    const handleLogin = (token: string) => {

        try {
            // Decode the JWT token
            const decoded: DecodedToken = jwtDecode(token);

            // Extract necessary information
            const userInfo: UserInfo = {
                username: decoded.preferred_username,
                names: decoded.name,
                email: decoded.email,
                id: decoded.sub,
                roles: decoded.resource_access.kyc_client?.roles || [],
                token: token,
                refreshToken:''
            };

            // Save user information to sessionStorage for easy access
            sessionStorage.setItem('userInfo', JSON.stringify(userInfo));

            // Optionally set username in cookies for basic tracking
            Cookies.set('username', userInfo.username, { expires: 1 / 24 }); // 1-hour expiration

            logIn()
            // Navigate to the forms page
            navigate('/home');
        } catch (error) {
            console.error('Error decoding token:', error);
            // toast.error('Invalid token. Please try logging in again.');
        }
    };

    const handleFakeLogin = (token: string) => {
        try {
            // Extract necessary information
            const userInfo: UserInfo = {
                username: "fake.admin",
                names: "Fake Admin",
                email: "fake.admin@btc.bw",
                id: "fake-admin-sdkjfdsom.sd.fsssadasdf",
                roles: ["write", "read","" ],
                token: token,
                refreshToken:''
            };
            // Save user information to sessionStorage for easy access
            sessionStorage.setItem('userInfo', JSON.stringify(userInfo));
            // Optionally set username in cookies for basic tracking
            Cookies.set('username', userInfo.username, { expires: 1 / 24 }); // 1-hour expiration
            logIn()
            // Navigate to the forms page
            navigate('/home');
        } catch (error) {
            console.error('Error decoding token:', error);
            // toast.error('Invalid token. Please try logging in again.');
        }
    };

    // Formik setup
    const formik = useFormik({
        validateOnBlur: true,
        initialValues: {
            username: '',
            password: '',
        },
        validationSchema: Yup.object({
            username: Yup.string().email('Invalid user email address')
                .required('Username is required'),
            password: Yup.string()
                .min(8, 'Password must be at least 8 characters')
                .required('Password is required'),
        }),
        onSubmit: async (values) => {
            setShowLoginError(false);
            try {
                // Mock login request (replace with real API call)
                // const response = await fakeLogin(values.username, values.password);
                const response = await submitLogin({username:values.username, password:values.password});
                if (response.success) {
                    handleLogin(response.result.access_token);
                    // handleFakeLogin("fakeTokenHereDoNotTrustItYouWilHarmTheApp");
                } else if (response.success === false){
                    setShowLoginError(true);
                }
            } catch (error) {
                // toast.error('An error occurred. Please try again.');
            }
        },
    });

    return (
        <div className="flex justify-center items-center min-h-screen bg-gray-100 p-4">
            <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-md">
                <div className="mb-8 text-center">
                    <img
                        src={logo}
                        alt="Company Logo"
                        className="h-16 mx-auto mb-4"
                    />
                    <h2 className="text-3xl font-bold text-gray-800">Welcome Back</h2>
                    <p className="text-gray-500 mt-2">Sign in to your account</p>
                </div>

                <form onSubmit={formik.handleSubmit}>
                    <div className="mb-4">
                        <label htmlFor="username" className="block text-sm font-medium text-gray-700">Username</label>
                        <div className="flex items-center border border-gray-300 rounded-lg p-2 mt-1">
                            {/*<FiUser className="text-gray-500 mr-2"/>*/}
                            <input
                                id="username"
                                name="username"
                                type="text"
                                className="w-full focus:outline-none"
                                placeholder="username@btc.bw"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.username}

                            />
                        </div>
                        {formik.touched.username && formik.errors.username ? (
                            <div className="text-red-600 text-sm mt-1">{formik.errors.username}</div>
                        ) : null}
                    </div>

                    <div className="mb-4">
                        <label htmlFor="password" className="block text-sm font-medium text-gray-700">Password</label>
                        <div className="flex items-center border border-gray-300 rounded-lg p-2 mt-1">
                            {/*<FiKey className="text-gray-500 mr-2"/>*/}
                            <input
                                id="password"
                                name="password"
                                type="password"
                                className="w-full focus:outline-none"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.password}
                            />
                        </div>
                        {formik.touched.password && formik.errors.password ? (
                            <div className="text-red-600 text-sm mt-1">{formik.errors.password}</div>
                        ) : null}
                        {showLoginError && <div className="text-red-600 text-sm mt-1"> Your password and / or username is incorrect!</div>}
                    </div>

                    <div className="mb-6 text-sm text-gray-600">
                        <p>If you're having trouble logging in, please ensure your AD account is not locked out.</p>
                    </div>

                    <button
                        type="submit"
                        className="w-full bg-green-600 text-white p-2 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50"
                    >
                        Login
                    </button>
                </form>
            </div>
        </div>
        // <div className="flex justify-center items-center h-screen bg-gray-100">
        //     <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
        //         <div className="mb-8 text-center">
        //             <img
        //                 src={logo}
        //                 alt="Company Logo"
        //                 className="h-16 mx-auto mb-4"
        //             />
        //             <h2 className="text-3xl font-bold text-gray-800">Welcome Back</h2>
        //             <p className="text-gray-500 mt-2">Sign in to your account</p>
        //         </div>
        //
        //         <form onSubmit={formik.handleSubmit}>
        //             <div className="mb-4">
        //                 <label htmlFor="username" className="block text-sm font-medium text-gray-700">Username</label>
        //                 <div className="flex items-center border border-gray-300 rounded-lg p-2 mt-1">
        //                     {/*<FiUser className="text-gray-500 mr-2"/>*/}
        //                     <input
        //                         id="username"
        //                         name="username"
        //                         type="text"
        //                         className="w-full focus:outline-none"
        //                         // className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
        //                         onChange={formik.handleChange}
        //                         onBlur={formik.handleBlur}
        //                         value={formik.values.username}
        //                     />
        //
        //                 </div>
        //                 {formik.touched.username && formik.errors.username ? (
        //                     <div className="text-red-600 text-sm">{formik.errors.username}</div>
        //                 ) : null}
        //             </div>
        //
        //             <div className="mb-4">
        //                 <label htmlFor="password" className="block text-sm font-medium text-gray-700">Password</label>
        //                 <div className="flex items-center border border-gray-300 rounded-lg p-2 mt-1">
        //                     {/*<FiKey className="text-gray-500 mr-2"/>*/}
        //                     <input
        //                         id="password"
        //                         name="password"
        //                         type="password"
        //                         className="w-full focus:outline-none"
        //                         onChange={formik.handleChange}
        //                         onBlur={formik.handleBlur}
        //                         value={formik.values.password}
        //                     />
        //                 </div>
        //
        //                 {formik.touched.password && formik.errors.password ? (
        //                     <div className="text-red-600 text-sm">{formik.errors.password}</div>
        //                 ) : null}
        //                 {showLoginError && <div className="text-red-600 text-sm"> Your password and / or username is incorrect!</div>}
        //             </div>
        //             <button
        //                 type="submit"
        //                 className="w-full bg-green-600 text-white p-2 rounded-md hover:bg-green-700"
        //             >
        //                 Login
        //             </button>
        //         </form>
        //     </div>
        // </div>
    );


};

// Mock login function (replace with real API call)
const fakeLogin = async (username: string, password: string) => {
    return new Promise<{ success: boolean }>((resolve) => {
        setTimeout(() => {
            resolve({ success: username === 'admin' && password === 'password123' });
        }, 500);
    });
};

export default LoginPage;
